import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, X, BookOpen, Save, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_LABELS = {
  snapshot: 'Snapshot Analysis',
  sfr: 'SFR Calculator',
  growth_model: 'Growth vs Model',
  growth_prognosis: 'Growth Prognosis',
  spreadsheet: 'Spreadsheet Comparison',
};

/**
 * CuratedExamplesPanel — admin-only management for the "Try [Species]"
 * preset buttons shown on every module page. Each cell in the matrix
 * (module × species) maps to ONE saved record (admin's own).
 *
 * Save flow: changing a dropdown stages the change as a *draft* (cell
 * highlighted amber). Nothing hits the backend until the admin clicks
 * the sticky "Save changes" footer button — at which point all drafts
 * are committed sequentially. Reset (the X button per cell) still
 * applies immediately because it removes a row, not edits one.
 */
export default function CuratedExamplesPanel() {
  const [token] = useState(() => localStorage.getItem('access_token') || localStorage.getItem('token'));
  const [items, setItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  // Per-cell source-records cache: `${module}__${species}` → records[]
  const [recordsCache, setRecordsCache] = useState({});
  // Draft (uncommitted) selections: `${module}__${species}` → recordId
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/curated-examples`, { headers: authHeader });
      setItems(r.data.items || []);
      setModules(r.data.modules || []);
      setSpecies(r.data.species || []);
      setDrafts({});
    } catch (err) {
      toast.error('Failed to load curated examples');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const itemFor = (module, sp) => items.find(i => i.module === module && i.species === sp);

  const loadSourceRecords = async (module, sp) => {
    const key = `${module}__${sp}`;
    if (recordsCache[key]) return;
    try {
      const r = await axios.get(`${API}/admin/curated-examples/source-records`, {
        params: { module, species: sp },
        headers: authHeader,
      });
      setRecordsCache(prev => ({ ...prev, [key]: r.data.records || [] }));
    } catch (err) {
      toast.error('Failed to load your saved records for this combo');
      setRecordsCache(prev => ({ ...prev, [key]: [] }));
    }
  };

  const stageDraft = (module, sp, recordId) => {
    if (!recordId) return;
    const key = `${module}__${sp}`;
    const current = itemFor(module, sp)?.source_record_id || '';
    setDrafts(prev => {
      const next = { ...prev };
      if (recordId === current) {
        delete next[key]; // reverted to saved value
      } else {
        next[key] = recordId;
      }
      return next;
    });
  };

  const draftCount = Object.keys(drafts).length;

  const handleSaveAll = async () => {
    if (draftCount === 0) return;
    setSavingAll(true);
    const entries = Object.entries(drafts);
    let okCount = 0;
    const errors = [];
    for (const [key, recordId] of entries) {
      const [module, sp] = key.split('__');
      try {
        await axios.put(`${API}/admin/curated-examples`,
          { module, species: sp, source_record_id: recordId },
          { headers: authHeader }
        );
        okCount += 1;
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = Array.isArray(detail) ? detail.map(d => d?.msg).join('; ') : (detail || 'Save failed');
        errors.push(`${MODULE_LABELS[module] || module} · ${sp}: ${msg}`);
      }
    }
    setSavingAll(false);
    if (okCount > 0) toast.success(`Saved ${okCount} curated example${okCount === 1 ? '' : 's'} — live across the platform`);
    if (errors.length > 0) toast.error(errors.join('\n'));
    await fetchAll();
  };

  const handleDiscardAll = () => {
    if (draftCount === 0) return;
    setDrafts({});
    toast.message(`Discarded ${draftCount} unsaved change${draftCount === 1 ? '' : 's'}`);
  };

  const handleReset = async (module, sp) => {
    if (!window.confirm(`Reset ${MODULE_LABELS[module]} · ${sp} back to the default seed?`)) return;
    try {
      await axios.delete(`${API}/admin/curated-examples/${module}/${sp}`, { headers: authHeader });
      toast.success('Reset to seed');
      // Drop any pending draft for this cell as it's now superseded
      setDrafts(prev => {
        const next = { ...prev };
        delete next[`${module}__${sp}`];
        return next;
      });
      await fetchAll();
    } catch (err) {
      toast.error('Reset failed');
    }
  };

  return (
    <Card data-testid="curated-examples-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-cyan-600" />
          Curated Examples
          {draftCount > 0 && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 ml-2" data-testid="curated-unsaved-count">
              {draftCount} unsaved
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Pick one of <strong>your</strong> saved records to power the "Try [Species]" button on each module.
          Changes are staged below — click <strong>Save changes</strong> at the bottom to publish them across the platform.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-6">
            {modules.map(module => (
              <div key={module} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800">{MODULE_LABELS[module] || module}</h3>
                  <Badge variant="outline" className="text-xs text-slate-500">
                    {species.length} species
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {species.map(sp => {
                    const it = itemFor(module, sp);
                    const key = `${module}__${sp}`;
                    const records = recordsCache[key];
                    const draftValue = drafts[key];
                    const isDirty = !!draftValue;
                    const selectedValue = draftValue || it?.source_record_id || '';
                    return (
                      <div
                        key={sp}
                        className={`rounded-md border p-3 transition-colors ${
                          isDirty
                            ? 'border-amber-300 bg-amber-50/60 ring-1 ring-amber-200'
                            : 'border-slate-200 bg-slate-50/50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="font-medium text-sm text-slate-800 flex items-center gap-2">
                              {sp}
                              {isDirty && (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs" data-testid={`curated-dirty-${module}-${sp.toLowerCase()}`}>
                                  Unsaved
                                </Badge>
                              )}
                            </div>
                            {it ? (
                              it.is_seed ? (
                                <Badge className="bg-amber-100 text-amber-800 border-amber-200 mt-1 text-xs">
                                  <Sparkles className="h-3 w-3 mr-1" /> Default seed
                                </Badge>
                              ) : (
                                <>
                                  <div
                                    className="text-xs font-semibold text-cyan-700 mt-1 truncate max-w-[220px]"
                                    title={it.source_record_name || '(unnamed record)'}
                                    data-testid={`curated-record-name-${module}-${sp.toLowerCase()}`}
                                  >
                                    File: {it.source_record_name || '(unnamed record)'}
                                  </div>
                                  <div className="text-[10px] text-slate-500 mt-0.5">
                                    Curated by {it.updated_by}
                                  </div>
                                </>
                              )
                            ) : (
                              <div className="text-xs text-slate-400 italic mt-1">Not curated</div>
                            )}
                          </div>
                          {it && !it.is_seed && (
                            <button
                              onClick={() => handleReset(module, sp)}
                              className="text-slate-400 hover:text-red-600"
                              title="Reset to default seed"
                              data-testid={`curated-reset-${module}-${sp.toLowerCase()}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        <Select
                          onOpenChange={(open) => { if (open) loadSourceRecords(module, sp); }}
                          value={selectedValue}
                          onValueChange={(v) => stageDraft(module, sp, v)}
                          disabled={savingAll}
                        >
                          <SelectTrigger className="w-full" data-testid={`curated-pick-${module}-${sp.toLowerCase()}`}>
                            <SelectValue placeholder="Pick one of your saved records…" />
                          </SelectTrigger>
                          <SelectContent>
                            {!records ? (
                              <div className="px-3 py-2 text-xs text-slate-500">Loading…</div>
                            ) : records.length === 0 ? (
                              <div className="px-3 py-2 text-xs text-slate-500">
                                No saved records of this type for {sp}. Run an analysis first.
                              </div>
                            ) : (
                              records.map(r => (
                                <SelectItem key={r.id} value={r.id}>
                                  {r.label}
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div
              className={`sticky bottom-3 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 backdrop-blur ${
                draftCount > 0
                  ? 'border-amber-300 bg-amber-50/90 shadow-sm'
                  : 'border-slate-200 bg-white/80'
              }`}
              data-testid="curated-save-bar"
            >
              <div className="text-sm">
                {draftCount > 0 ? (
                  <span className="text-amber-800 font-medium">
                    {draftCount} unsaved change{draftCount === 1 ? '' : 's'} — click Save to publish.
                  </span>
                ) : (
                  <span className="text-slate-500">All changes saved.</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchAll}
                  disabled={savingAll}
                  data-testid="curated-refresh-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDiscardAll}
                  disabled={draftCount === 0 || savingAll}
                  data-testid="curated-discard-btn"
                >
                  <Undo2 className="h-4 w-4 mr-1" /> Discard
                </Button>
                <Button
                  size="sm"
                  onClick={handleSaveAll}
                  disabled={draftCount === 0 || savingAll}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="curated-save-btn"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {savingAll ? 'Saving…' : `Save changes${draftCount > 0 ? ` (${draftCount})` : ''}`}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
